/**
 * Screening recommendation component
 */
import { Component, OnInit, ElementRef, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, Inject } from '@angular/core';
import { IdynamicFormItem } from '@ispm/dynamic-form/interfaces/IdynamicFormItem';
import { Widget } from '@ispm/widgets/decorator/widget.decorator';
import { IWidgetItem } from '@ispm/widgets/interfaces/widget-item.interface';
import { FormulaService } from '@ispm/shared/services/formula.service';
import { UserActionCreatorService } from '@ispm/user/services/user-action-creator.service';
import { get, filter, isEqual, has, isEmpty, cloneDeep, find } from 'lodash';
import { DynamicModalLoaderService } from '@ispm/layout/services/dynamic-modal-loader.service';
import { PlaceholderModalComponent } from '@ispm/layout/components/modals/placeholder-modal/placeholder-modal.component';
import { PlaceholderModalLoaderPayload } from '@ispm/layout/classes/placeholder-modal-loader-payload';
import { ScreeningGuidelineComponent } from '../guideline/screening-guideline.component';
import { ScreeningService } from '../../services/screening.service';
import { FormGroup } from '@angular/forms';
import { AgregationService } from '@ispm-client/modules/aggregator/services';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FormHandlerService } from '@ispm/dynamic-form/services/form-handler.service';
import { Observable } from 'rxjs/Observable';
import { AuthenticationService } from '@ispm/user/services/authentication.service';
import { ActivatedRoute } from "@angular/router";

@Component({
  selector: 'screening-recommendation',
  templateUrl: './screening-recommendation.component.html',
  styleUrls: ['./screening-recommendation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

@Widget('ScreeningRecommendation')
export class ScreeningRecommendationComponent implements OnInit, OnDestroy, IWidgetItem {
  widgetUpdateValueCallback: (value) => void;
  config: { type: string; };
  data: any;
  riskStatus: string;
  recommendationSuggestion: string;
  recommendationGuidelines: any = { };
  private patientId: any;
  private patientBirthDate: any;
  private formulaData: any = { };
  private formulaExpression: string = '';
  private formulaFields: any;
  private guidelineVersion: string;
  private id: string;
  recommendationType: string = '';
  recommendationLabel: string;
  recommendationDefaultMsg: string = 'Further input required';
  public widgetFormInstance: FormGroup;
  activeRecommendation = { };
  private configTrigger$: Subject<any> = new Subject<any>();
  private _destroyer: Subject<any> = new Subject<any>();
  public isEditMode: boolean = false;
  private recommendationConfigMap: Map<string, any> = new Map<string, any>();
  private currentWorkflow;

  constructor(
    private userActionService: UserActionCreatorService,
    private readonly elementRef: ElementRef,
    private modalLoader: DynamicModalLoaderService,
    private _changeDetector: ChangeDetectorRef,
    private aggregatorService: AgregationService,
    private formHandler: FormHandlerService,
    private _screeningService: ScreeningService,
    private authService: AuthenticationService,
    private activatedRoute: ActivatedRoute) {
    this.patientId = this.activatedRoute.parent.parent.snapshot.params.patientId;
    this.userActionService.getPatient(this.patientId).pipe(takeUntil(this._destroyer.asObservable()))
      .subscribe(patientData => {
        this.patientBirthDate = patientData.birthDate;
      });
  }

  ngOnInit() {
  }

  /**
   * set card dashboard widget config
   * @param _itemConfig
   */
  set widgetConfig(_itemConfig: IdynamicFormItem) {
    if (!isEqual(this.config, _itemConfig.widgetPayload)) {
      this.config = _itemConfig.widgetPayload;
      this.id = get(this.config, 'id');
      this.recommendationType = get(this.config, 'type');
      this.recommendationLabel = get(this.config, 'label');
      const stepsToGetCMSLatestVersion = get(this.config, 'stepsToGetCMSLatestVersion');
      this.fetchDataElementResource(stepsToGetCMSLatestVersion);
      const requiredPermissionsIds = get(this.config, 'requiredPermissionsIds');
      if (requiredPermissionsIds) {
        this.isEditMode = this.authService.doesUserHasPermission(get(requiredPermissionsIds, 'write'));
      }
    }
  }

  /**
   * set card dashboard widget data
   * @param _widgetData
   */
  set widgetData(_widgetData: any) {
    if (!isEmpty(_widgetData) && !isEqual(this.data, _widgetData)) {
      this.data = cloneDeep(_widgetData);
      this.data['birthDate'] = this.patientBirthDate;
      this.calculateRecommendationResults(this.data);
      const configSubscription = this.configTrigger$.asObservable().take(1).subscribe(() => {
        this.calculateRecommendationResults(this.data);
        if (configSubscription) {
          configSubscription.unsubscribe();
        }
      });
    }
  }

  /**
   * Get specific CMS version based n step else default fallback
   * @param stepsConfig
   * @param currentWorkflowStep
   */
  getCmsConfig(stepsConfig, currentWorkflowStep): Observable<any> {
    const guidelineUpdatedDate = new Date('July 30, 2023 23:59:00').getTime();
    const workflowCompletionDate = new Date(get(currentWorkflowStep, 'meta.lastUpdated', '')).getTime();
    const workflowLastUpdatedDay = new Date(get(currentWorkflowStep, 'meta.lastUpdated', '')).toDateString();
    const workflowCreatedDay = new Date(get(currentWorkflowStep, 'meta.created', '')).toDateString()
    const workflowContext = get(currentWorkflowStep, 'context', []);
    const workflowStatus = get(currentWorkflowStep, 'latestRevisionStatus', '');
    let guidelineVersion ="";
    if((workflowCompletionDate <= guidelineUpdatedDate) || (find(workflowContext, function(cntx) {return get(cntx, 'resourceType') == "sourceTransactionUUID"}) && ["Completed", "Stopped"].includes(workflowStatus) && workflowLastUpdatedDay == workflowCreatedDay)) {
      guidelineVersion = "V2015";
    }
    const cmsConfig$ = this._screeningService.getBundle(`context=[${this.patientId},${get(currentWorkflowStep, 'id', '')}]&subResourceType=GUIDELINE`, 'Observation', false)
    .switchMap(observationRes => {
      if (!isEmpty(observationRes) && !isEmpty(observationRes['guidelineVersion']) && !stepsConfig.includes(workflowStatus)) {
        guidelineVersion = guidelineVersion ? guidelineVersion: observationRes['guidelineVersion']
        return this._screeningService.getBundle(`version=[${guidelineVersion}]`, 'DataElement',true)
          .map(data => {
            return data;
          });
      } else {
        if(guidelineVersion) {
          return this._screeningService.getBundle(`version=[${guidelineVersion}]`, 'DataElement',true)
          .map(data => {
            return data;
          });
        } else {
          return this._screeningService.getCmsConfiguration({ type: 'cms', context: ['cmsConfiguration'], resourceType: 'DataElement'},"cms+cmsConfiguration")
          .map(res => {
            return res;
          });
        }
      }
    });
    return cmsConfig$;
  }

  async fetchDataElementResource(stepsConfig) {
    if (stepsConfig) {
      this.currentWorkflow =  await this._screeningService.getLatestRevisionStatus().then(res => {
         return res;
      });
      this.getCmsConfig(stepsConfig, this.currentWorkflow).pipe(takeUntil(this._destroyer.asObservable()))
        .subscribe(cmsConfig => {
        this.recommendationConfigMap.set(this.recommendationType, cmsConfig);
        this.recommendationGuidelines = get(cmsConfig, 'recommendationGuidelines');
        this.configTrigger$.next(null);
      });
    }
  }

  /**
   * Evaluate the formula based on configured params
   * @param data
   */
  private calculateRecommendationResults(data: any) {
    if (this.recommendationConfigMap.size >= 1) {
      this.activeRecommendation = this.recommendationConfigMap.get(this.recommendationType);
      this.formulaFields = get(this.activeRecommendation, 'fieldsToUse');
      this.formulaExpression = get(this.activeRecommendation, 'formula');
      this.guidelineVersion = get(this.activeRecommendation, 'version');

      if (this.formulaFields && (this.formulaFields.length) > 0) {
        this.formulaFields.forEach(value => {
          let elementData = get(data, value.pathToPick, value.defaultValue);
          if (Array.isArray(elementData)) {
            elementData = (elementData.length) > 0 ? filter(elementData, function (o) {
              return get(o, 'selected');
            }) : elementData;
            this.formulaData[value.formulaField] = elementData;
          } else {
            this.formulaData[value.formulaField] = elementData;
          }
        });
      } else {
        this.formulaData = data;
      }

      if (this.formulaExpression && this.formulaData) {
        const value = FormulaService.evaluateFormula(this.formulaExpression, this.formulaData);
        if (value) {
          if ((value.recommendation !== this.recommendationSuggestion) ||
          (value.risk !== this.riskStatus)) {
            this.recommendationSuggestion = value.recommendation;
            this.riskStatus = value.risk;
            if (this.isEditMode && !isEqual(this.recommendationSuggestion, this.recommendationDefaultMsg)) {
              this.saveRecommendationsFormData();
            }
          }
        }
      }

      this.addListenerForGuideLine();
      this._changeDetector.markForCheck();

      /*
          this.recommendationOutputData = {
            "guideline": this.config.type,
            "riskStatus": this.riskStatus,
            "guidelineVersion": "NCCN V2020",
            "recommendation": this.recommendationSuggestion
          }

          if (this.recommendationType) {
            data["recommendationType"] = this.recommendationOutputData;

            //  this.widgetFormInstance.controls["backUp"].setValue(this.recommendationOutputData);
            //  this.widgetUpdateValueCallback(data);
            // this._changeDetector.markForCheck();
            // this._changeDetector.detectChanges();
          }*/
   }
  }

  private saveRecommendationsFormData() {
    if (this.config) {
      const dataBindingFormId = get(this.config, 'dataBindingFormId');
      this.formHandler.getForm(dataBindingFormId).subscribe(databindingObj => {
        const formId = databindingObj['id'];
        if (has(databindingObj, 'dataBinding')) {
          const dataBinding =  databindingObj['dataBinding'];
          if (dataBinding[0] && dataBinding[0].queryString) {
            const queryString = `context=[${this.patientId},${get(this.currentWorkflow, 'id', '')}]&${get(dataBinding, '[0].queryString', '')}`
            const querySubscription = this._screeningService.getBundle(queryString, dataBinding[0].targetResource,false).subscribe(actualData => {
              actualData  = actualData ? actualData : {}
                actualData.riskStatus = this.riskStatus;
                actualData.guidelineType = this.recommendationType;
                actualData.category = get(this.config, 'category');
                actualData.guidelineVersion = this.guidelineVersion,
                actualData.recommendation = this.recommendationSuggestion;
                actualData.formId = formId;
                actualData.subResourceType = get(this.config, 'subResourceType');
                let context = get(actualData, 'context', []);
                if(context.find(item => get(item, 'resourceType', '').toLowerCase() == 'workflowrequest') == undefined) {
                  context.push({'resourceType': 'WorkflowRequest', 'reference': get(this.currentWorkflow, 'id')});
                }
                this.aggregatorService.saveFormData(formId, actualData, get(actualData, 'id', false) ? []: context).subscribe(savedData => savedData);
              if (querySubscription) {
                querySubscription.unsubscribe();
              }
            });
          }
        }
      });
    }
  }

  private addListenerForGuideLine() {
    if (this.elementRef.nativeElement.querySelector('.guideline-message')) {
      this.elementRef.nativeElement.querySelector('.guideline-message').addEventListener('click', (event) => {
        const classList = event.target.classList;
        if (classList && classList.contains('guidelinetext')) {
          this.openPopupModal();
        }
      });
    }
  }


  private openPopupModal() {
    this.modalLoader.popupModal(PlaceholderModalComponent, PlaceholderModalLoaderPayload.builder()
      .setComponentToLoad(ScreeningGuidelineComponent)
      .setTitle(this.recommendationGuidelines.guidelineTitle.toString())
      .setCssClasses('fixed-size-modal-container-edit')
      .setPayload({
        guideLineExternalLink: this.recommendationGuidelines.externalLink,
        guideLineImage: this.recommendationGuidelines.guidelineFile,
        guidelineLinkTitle: this.recommendationGuidelines.guidelineLinkTitle
      })
    ).pipe(takeUntil(this._destroyer.asObservable()))
      .subscribe(data => { });
  }

  ngOnDestroy(): void {
    this.data = null;
    this._destroyer.next();
    this._destroyer.complete();
  }
}
